import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Brain, TrendingUp, TrendingDown, Flame, Award, ArrowRight, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = { 'Not Started': '#A1A1AA', 'Developing': '#71717a', 'Building': '#52525b', 'Proficient': '#27272a', 'Advanced': '#09090B' };

function ScoreGauge({ score, maxScore }) {
  const pct = (score / maxScore) * 100;
  const circumference = 2 * Math.PI * 120;
  const offset = circumference - (pct / 100) * circumference * 0.75; // 270 degree arc
  return (
    <div className="relative w-64 h-64 mx-auto">
      <svg viewBox="0 0 260 260" className="w-full h-full -rotate-[135deg]">
        <circle cx="130" cy="130" r="120" fill="none" stroke="#F4F4F5" strokeWidth="12" strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`} />
        <circle cx="130" cy="130" r="120" fill="none" stroke="#09090B" strokeWidth="12" strokeLinecap="butt"
          strokeDasharray={`${circumference * 0.75} ${circumference * 0.25}`}
          strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-6xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }} data-testid="brainmap-total-score">
          {score}
        </span>
        <span className="mono-data text-xs text-zinc-400 mt-1">/ {maxScore}</span>
      </div>
    </div>
  );
}

export default function BrainMapProfile() {
  const { user } = useAuth();
  const [brainmap, setBrainmap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/brainmap/score`, { withCredentials: true })
      .then(res => setBrainmap(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="mono-data text-lg animate-pulse-red">Calculating BrainMap...</div></div>;
  }

  if (!brainmap || brainmap.total_score === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="brainmap-empty">
        <div className="text-center max-w-md">
          <Brain className="w-16 h-16 text-[#09090B] mx-auto mb-6" />
          <h2 className="text-3xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Your BrainMap</h2>
          <p className="text-zinc-600 mb-2">Complete your first session to generate your BrainMap Score.</p>
          <p className="text-sm text-zinc-400 mb-8">Your score measures 5 cognitive dimensions on a 0-900 scale.</p>
          <Link to="/programs" className="btn-accent">Start Training</Link>
        </div>
      </div>
    );
  }

  const dims = brainmap.dimensions;
  const radarData = Object.entries(dims).map(([key, d]) => ({
    subject: d.label.split(' ')[0],
    score: Math.round(d.score / d.max * 100),
    fullMark: 100
  }));

  const historyData = (brainmap.history || []).map((h, i) => ({
    name: `S${i + 1}`,
    score: h.score
  }));

  return (
    <div className="min-h-screen" data-testid="brainmap-page">
      {/* Header */}
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss flex items-center justify-between">
          <div>
            <p className="mono-data text-[#09090B] mb-1">Cognitive Assessment</p>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>BrainMap Score</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="mono-data text-xs text-zinc-500">Level:</span>
            <span className="px-3 py-1 text-white text-xs font-bold tracking-widest uppercase" style={{ background: LEVEL_COLORS[brainmap.level] }} data-testid="brainmap-level">
              {brainmap.level}
            </span>
          </div>
        </div>
      </div>

      {/* Main Score */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss py-12 text-center">
          <ScoreGauge score={brainmap.total_score} maxScore={900} />
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-[#09090B]" />
              <span className="mono-data text-sm">{brainmap.streak} day streak</span>
            </div>
            <div className="flex items-center gap-2">
              {brainmap.improvement_trend >= 0 ? <TrendingUp className="w-4 h-4 text-[#09090B]" /> : <TrendingDown className="w-4 h-4 text-[#09090B]" />}
              <span className="mono-data text-sm">{brainmap.improvement_trend > 0 ? '+' : ''}{brainmap.improvement_trend}% trend</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-[#09090B]" />
              <span className="mono-data text-sm">Top {100 - brainmap.percentile}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dimension Breakdown */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss py-8">
          <h3 className="mono-data text-sm text-[#09090B] mb-6">Cognitive Dimensions</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#09090B]">
            {/* Radar */}
            <div className="p-6 lg:border-r border-b lg:border-b-0 border-[#09090B]" data-testid="brainmap-radar">
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E4E4E7" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fontFamily: 'IBM Plex Sans' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar name="Score" dataKey="score" stroke="#09090B" fill="#09090B" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Dimension bars */}
            <div className="p-6" data-testid="brainmap-dimensions">
              <div className="space-y-5">
                {Object.entries(dims).map(([key, d]) => {
                  const pct = Math.round(d.score / d.max * 100);
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium">{d.label}</span>
                        <span className="mono-data text-xs font-bold">{d.score}/{d.max}</span>
                      </div>
                      <div className="h-2 bg-zinc-200 w-full">
                        <div className="h-full bg-[#09090B] transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Chart */}
      {historyData.length > 1 && (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss py-8">
            <h3 className="mono-data text-sm text-[#09090B] mb-4">Score Progression</h3>
            <div className="border border-[#09090B] p-6" data-testid="brainmap-history">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={historyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="score" stroke="#09090B" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="container-swiss py-8">
        <div className="border border-[#09090B] bg-zinc-100 p-4 mb-6 max-w-2xl flex gap-3 items-start" data-testid="medical-disclaimer">
          <span className="mono-data text-[10px] text-[#09090B] font-bold whitespace-nowrap">DISCLAIMER</span>
          <p className="text-xs text-zinc-700 leading-relaxed">
            <strong>BrainMap Score is an educational tool, not a medical or diagnostic assessment.</strong>{' '}
            It does not diagnose, treat, or evaluate any neurological, psychological, or learning condition.
            Consult a qualified professional for clinical concerns.
          </p>
        </div>
        <div className="border border-[#09090B] p-6 max-w-2xl" data-testid="brainmap-info">
          <h3 className="mono-data text-sm mb-3 flex items-center gap-2"><Zap className="w-3 h-3 text-[#09090B]" /> How BrainMap Score Works</h3>
          <p className="text-sm text-zinc-600 leading-relaxed mb-3">
            Your BrainMap Score (0-900) measures cognitive performance across 5 scientifically-grounded dimensions.
            Each dimension contributes up to 180 points. The score updates after every completed session.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {[['0-300', 'Developing', '#71717a'], ['301-500', 'Building', '#52525b'], ['501-700', 'Proficient', '#27272a'], ['701-900', 'Advanced', '#09090B']].map(([range, label, color]) => (
              <div key={label} className="flex items-center gap-2">
                <div className="w-2 h-2" style={{ background: color }} />
                <span className="mono-data">{range}: {label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
